import type { IBaseAttributes, TConstraints } from '@feathr/rachis';
import { Collection, Model } from '@feathr/rachis';

/**
 * One address an export may be delivered to. Deliberately narrower than
 * `IUser`: anhinga gates this route on `segments` rather than `users`, and
 * serves three fields so a caller who cannot read the account's users still
 * cannot read them here.
 */
export interface ISegmentExportRecipient extends IBaseAttributes {
  /**
   * Null for a member the account holds no name for. The picker labels an
   * option `Name (email)`, or the address alone when this is null — the address
   * is always shown because it is what the export is delivered to, and two
   * members can share a name.
   */
  name: string | null;
  email: string;
}

export class SegmentExportRecipient extends Model<ISegmentExportRecipient> {
  public readonly className = 'SegmentExportRecipient';

  public get constraints(): TConstraints<ISegmentExportRecipient> {
    return {
      email: {
        presence: { allowEmpty: false },
      },
    };
  }
}

/**
 * The addresses this account's exports can be delivered to — its non-archived
 * members. anhinga builds the list from the same membership definition the
 * export create path validates against, so every address offered here is one
 * `POST /segment-exports/` accepts. See anhinga#8540.
 */
export class SegmentExportRecipients extends Collection<SegmentExportRecipient> {
  public getModel(attributes: Partial<ISegmentExportRecipient>): SegmentExportRecipient {
    return new SegmentExportRecipient(attributes);
  }

  public getClassName(): string {
    return 'segment-exports/recipients';
  }

  /**
   * Drops the trailing slash `Collection.url` appends. anhinga binds the rule
   * as `/segment-exports/recipients`, and Werkzeug redirects a *missing*
   * trailing slash but never an extra one, so `.../recipients/` would 404.
   */
  public override url(): string {
    return `${this.getHostname()}${this.getClassName()}`;
  }
}
